import EventBus from "@/commonui/utils/eventBus";
import { CommonLocale, ConversationBuilderLocale } from "@/locales/localeid";
import { AdminService, ContainerType, SignatureResoureModel } from "@/services/admin";
import {
	TestConversationDetailsDto,
	ConversationBuilderService,
	ConversationDetailResponseModel,
	CopyImageRequestModel,
	InteractionStyleModel,
	UpsertConversationRequestModel,
	VoiceGenStatus
} from "@/services/conversation-builder";
import { BuilderPageType } from "@/util/enum";
import { deepClone, getActualFileNameAndType, isOldConversationImage, showErrorMessage } from "@/util/func";
import {
	assignNewIds,
	generateDefaultConversation,
	invalidateEntityValueIdsInPath,
	mergeCommonEntitiesIntoAppEntities,
	mergeCommonIntentIntoAppIntents,
	updateMetaData
} from "@/views/conversation-builder/conversation-builder-util";
import {
	BuilderEventType,
	Connector,
	Conversation,
	ConversationNode,
	ConversationNodeImagePath,
	ConversationNodeType,
	Dialog,
	EntityDataSet,
	FileUploads,
	getEntitySetDefaultValue,
	IConnectorAdd,
	IMetaData,
	INodes,
	INodeUpdate,
	NodeData,
	Path
} from "@/views/conversation-builder/conversation-builder.model";
import { CommonEntity, Entity } from "@/views/conversation-builder/models/entity.model";
import { CommonIntent, Intent } from "@/views/conversation-builder/models/intent.model";
import { AnonymousCredential, BlobServiceClient, BlockBlobClient, newPipeline } from "@azure/storage-blob";
import {
	fmtMsg,
	store,
	MessageHelper,
	NotificationType,
	GLGlobal,
	RoleName,
	Role,
	ConversationPublishStatus
} from "commonui";
import FileSaver from "file-saver";
import { cloneDeep, differenceBy, includes, omit, pull } from "lodash";
import moment, { Moment } from "moment";
import { v4 } from "uuid";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { Modal } from "ant-design-vue";
import { AccountService } from "@/services";
import { ImageMetaData } from "@/models/image-meta-data";
import { CLUValidations } from "@/util/constants";

@Module({ namespaced: true, name: "ConversationBuilder", store: store, dynamic: true })
export class ConversationBuilderState extends VuexModule {
	nodes: INodes = {};
	entities: EntityDataSet<Entity> = getEntitySetDefaultValue();
	intents: EntityDataSet<Intent> = getEntitySetDefaultValue();

	commonEntites: EntityDataSet<CommonEntity> = getEntitySetDefaultValue();

	commonIntents: EntityDataSet<CommonIntent> = getEntitySetDefaultValue();

	interactionStyles: InteractionStyleModel[] = [];

	changedSinceLastSave = false; // keeps the last state saved in DB
	changes: string[] = [];

	/** @deprecated: Please use selectedNodes rather selectedNode, it is likely to be removed. */
	selectedNode: string | null = null;

	selectedNodes: { [key: string]: boolean } = {};
	conversationProperties: Conversation = generateDefaultConversation();
	conversationExtraInfo: Partial<ConversationDetailResponseModel> = {};
	conversationTestExtraInfo: Partial<ConversationDetailResponseModel> = {};
	savingConversation = false; // if 'saveConversationState' is being executed.
	createOrPublisingConversation = false; // if creating or publising the conversation to luis.
	createOrPublisingTestConversation = false; // if publishing to conversation as test.
	appTrainStatus = null; // #TODO
	currentPublishStatus: ConversationPublishStatus = ConversationPublishStatus.None;
	currentTestPublishStatus: ConversationPublishStatus = ConversationPublishStatus.None;
	testVoiceGenStatus: VoiceGenStatus = VoiceGenStatus.None;
	conversationVoiceGenStatus: VoiceGenStatus = VoiceGenStatus.None;
	isConvNameUnique = true;
	isConvNameLengthValid = true;
	isConvNameValid = true;

	// SAS Url information
	sasURL: SignatureResoureModel[] | null = null;
	sasURLFetchedAt: Moment | null = null;
	downloadSASURL: SignatureResoureModel[] | null = null;
	downloadSASURLFetchAt: Moment | null = null;

	// image upload infor
	fileUploads: FileUploads[] = []; // keeps track of nodes for which images are being uploaded

	// master templates id
	masterTemplateId: number | null = null;
	isConversationComplete: boolean = false;

	testConversationChanges: string[] = [];
	testConverationMetaData: { changes?: string[] } = {};
	isUXTestingAllowed: boolean = false;

	// speed control
	public validSpeeds = { 0: "0.6x", 25: "0.7x", 50: "0.8x", 75: "0.9x", 100: "1x" };
	public currentSpeed = 100;

	@Mutation
	setCurrentSpeed(value: number) {
		this.currentSpeed = value;
	}

	@Mutation
	private createNode<T = NodeData>(node: ConversationNode<T>): void {
		// add order field
		const nodeAsDialog = (node as any) as ConversationNode<Dialog>;
		switch (node.type) {
			case ConversationNodeType.Dialog:
			case ConversationNodeType.Path: {
				const orderCollection = Object.entries(this.nodes)
					.filter(([key, iteratedNode]) => iteratedNode.type === node.type)
					.map(([key, iteratedNode]) => {
						const dialog = iteratedNode as ConversationNode<Dialog>;
						return dialog.data.order;
					});
				const maxOrder = orderCollection.length ? Math.max(...orderCollection) : 0;
				nodeAsDialog.data.order = maxOrder + 1;
				break;
			}

			case ConversationNodeType.Conversation: {
				nodeAsDialog.data.order = 0;
			}
		}

		// save node
		this.nodes = { ...this.nodes, [node.id]: node } as {
			[key: string]: ConversationNode;
		};
	}

	@Mutation
	private editNode<T = NodeData>(update: INodeUpdate<T>): void {
		if (update?.id && update?.data && this.nodes[update.id]?.data) {
			this.nodes = {
				...this.nodes,
				[update.id]: { ...this.nodes[update.id], data: { ...this.nodes[update.id].data, ...update.data } }
			};
		}
	}

	@Mutation
	private deleteNode(nodeId: string): void {
		const node = this.nodes[nodeId];
		// Unselect the current node
		this.selectedNode = null;
		delete this.selectedNodes[nodeId];

		for (const key in this.nodes) {
			const currentNode = this.nodes[key];

			// Removing sourceId/targetId from connector
			if (currentNode.type === ConversationNodeType.Connector) {
				const connector = currentNode.data as Connector;
				if (connector.targetId === nodeId) {
					connector.targetId = undefined;
				}
				if (connector.sourceId === nodeId) {
					delete this.nodes[currentNode.id];
				}
			}
		}

		if (node.type === ConversationNodeType.Path) {
			// all intents used by the path.
			const intentIds = (node as ConversationNode<Path>).data.intents.map((i) => i.id);

			intentIds.forEach((intentId) => {
				const otherPathIdsThatUseSameIntent = Object.keys(this.nodes).filter(
					(key) =>
						key !== node.id &&
						this.nodes[key] &&
						this.nodes[key].type === ConversationNodeType.Path &&
						(this.nodes[key].data as Path).intents.some((i) => i.id === intentId)
				);

				// If no other path uses that intent, delete that intent from directory.
				if (!otherPathIdsThatUseSameIntent || !otherPathIdsThatUseSameIntent.length) {
					const entityId = this.intents.directory[intentId] && this.intents.directory[intentId].entityId;

					this.intents = {
						ids: pull(this.intents.ids, intentId),
						directory: omit(this.intents.directory, [intentId])
					};

					// If intent used an entity and that entity is not referenced in any other intent, delete that entity too.
					if (entityId) {
						const otherIntentIdsThatUseSameEntity = this.intents.ids.filter(
							(id) =>
								id !== intentId &&
								this.intents.directory[id] &&
								this.intents.directory[id].entityId == entityId
						);

						if (!otherIntentIdsThatUseSameEntity || !otherIntentIdsThatUseSameEntity.length) {
							this.entities = {
								ids: pull(this.entities.ids, entityId),
								directory: omit(this.entities.directory, [entityId])
							};
						}
					}
				}
			});
		}

		// Deleting the node
		delete this.nodes[nodeId];

		// Update order field for dialog and intnet type of nodes
		if (node.type === ConversationNodeType.Dialog || node.type === ConversationNodeType.Path) {
			const orderMap = Object.entries(this.nodes)
				.filter(([_, n]) => n.type === node.type)
				.map((m, index) => ({ id: m[1].id, order: index + 1 }));

			const updatedNodes = Object.entries(this.nodes)
				.map((m, index) => {
					if (m[1].type === node.type) {
						const updateNode = m[1] as ConversationNode<Dialog | Path>;
						const updatedOrder = orderMap.find((map) => map.id === updateNode.id);

						if (updatedOrder) {
							updateNode.data.order = updatedOrder.order;
						}

						return { [m[0]]: updateNode as ConversationNode };
					}

					return { [m[0]]: m[1] };
				})
				.reduce((acc, current) => ({ ...acc, ...current }), {});

			// assigning
			this.nodes = updatedNodes;
		} else {
			// Reassigning
			this.nodes = { ...this.nodes };
		}
	}

	@Mutation
	private createConnector(update: IConnectorAdd): void {
		this.nodes = {
			...this.nodes,
			[update.connectorId]: {
				...this.nodes[update.connectorId],
				data: { ...this.nodes[update.connectorId].data, targetId: update.targetId }
			}
		};
	}

	@Mutation
	commitCoords(update: { id: string; x: number; y: number }[]) {
		const newNodes = { ...this.nodes };
		update.forEach((up) => {
			newNodes[up.id] = { ...newNodes[up.id], coords: { x: up.x, y: up.y } };
		});
		this.nodes = newNodes;
	}

	@Mutation
	setSelectNode(data: { id: string | null; isCtrlKey?: boolean }) {
		const { id, isCtrlKey } = data;

		this.selectedNode = id;

		// Multi-selection
		if (isCtrlKey && id) {
			const alreadyExists = this.selectedNodes[id];
			if (alreadyExists) {
				delete this.selectedNodes[id];
				this.selectedNodes = { ...this.selectedNodes };
			} else {
				this.selectedNodes = { ...this.selectedNodes, [id]: true };
			}
		} else if (id) {
			this.selectedNodes = { [id]: true };
		} else {
			this.selectedNodes = {};
		}
	}

	@Mutation
	setSelectNodes(ids: string[]) {
		// Single selection
		this.selectedNode = ids[0];

		// Multi-selection
		const selectedIdsUnordered = {};
		ids.forEach((id) => (selectedIdsUnordered[id] = true));
		this.selectedNodes = selectedIdsUnordered;
	}

	@Mutation
	setMetaData(value: {
		changes?: string[];
		nodes?: INodes;
		intents?: EntityDataSet<Intent>;
		entities?: EntityDataSet<Entity>;
	}) {
		this.nodes = value.nodes || {};
		this.changes = value.changes || [];
		this.intents = value.intents || getEntitySetDefaultValue();
		this.entities = value.entities || getEntitySetDefaultValue();
		this.changedSinceLastSave = false;
	}

	@Mutation
	setCommonEntitiesIntentsAndStyles(action: {
		entities: CommonEntity[];
		intents: CommonIntent[];
		styles: InteractionStyleModel[];
	}) {
		this.commonEntites = getEntitySetDefaultValue<CommonEntity>(action.entities);
		this.commonIntents = getEntitySetDefaultValue<CommonIntent>(action.intents);
		this.interactionStyles = action.styles;
	}

	@Mutation
	private trackChanges(nodeId: string) {
		if (this.changes.indexOf(nodeId) === -1) {
			this.changes.push(nodeId);
		}
		if (this.testConversationChanges.indexOf(nodeId) === -1 && this.conversationTestExtraInfo.conversationId) {
			this.testConversationChanges.push(nodeId);
		}
	}

	@Mutation
	public addTemplate(metadata: INodes) {
		this.nodes = { ...this.nodes, ...metadata };
	}

	@Mutation
	resetState() {
		this.nodes = {};
		this.intents = getEntitySetDefaultValue();
		this.entities = getEntitySetDefaultValue();
		this.commonEntites = getEntitySetDefaultValue();
		this.commonIntents = getEntitySetDefaultValue();
		this.changedSinceLastSave = false;
		this.selectedNode = null;
		this.selectedNodes = {};
		this.conversationExtraInfo = {};
		this.conversationTestExtraInfo = {};
		this.conversationProperties = generateDefaultConversation();
		this.fileUploads = [];
		this.changes = [];
		this.testConversationChanges = [];
		this.isUXTestingAllowed = false;
		this.masterTemplateId = null;
		this.isConversationComplete = false;
		this.currentSpeed = 100;
		this.conversationVoiceGenStatus = VoiceGenStatus.None;
		this.testVoiceGenStatus = VoiceGenStatus.None;
		this.currentPublishStatus = ConversationPublishStatus.None;
		this.currentTestPublishStatus = ConversationPublishStatus.None;
	}

	@Mutation
	setChangedSinceLastSave(value: boolean) {
		this.changedSinceLastSave = value;
	}

	@Mutation
	setConversationProps(value: Partial<Conversation>) {
		this.conversationProperties = { ...this.conversationProperties, ...value };
	}

	@Mutation
	setConversationExtraInfo(value: any) {
		this.conversationExtraInfo = value;
	}

	@Mutation
	setUploadsRemaining(value: FileUploads[]) {
		this.fileUploads = value;
	}

	@Mutation
	setSasURL(value: SignatureResoureModel[]) {
		this.sasURL = value;
		this.sasURLFetchedAt = moment();
	}

	@Mutation
	setDownloadSasURL(value: SignatureResoureModel[]) {
		this.downloadSASURL = value;
		this.downloadSASURLFetchAt = moment();
	}

	@Mutation
	updateUploadProgress(args: { loadedBytes: number; uploadId: string; self: ConversationBuilderState }) {
		const { loadedBytes, uploadId, self } = args;
		const uploads = self.fileUploads.map((f) => {
			if (f.id === uploadId) {
				f.uploaded = loadedBytes;
			}
			return f;
		});
		self.setUploadsRemaining(uploads);
	}

	@Mutation
	setSavingConversation(value: boolean) {
		this.savingConversation = value;
	}

	@Mutation
	setCreateOrPublishingConversation(value: boolean) {
		this.createOrPublisingConversation = value;
	}

	@Mutation
	setCreateOrPublisingTestConversation(value: boolean) {
		this.createOrPublisingTestConversation = value;
	}

	@Mutation
	setTrainingStatus(value: ConversationPublishStatus) {
		this.currentPublishStatus = value;
	}

	@Mutation
	setTestTrainingStatus(value: ConversationPublishStatus) {
		this.currentTestPublishStatus = value;
	}

	@Mutation
	setTestVoiceGenerationStatus(value: VoiceGenStatus) {
		this.testVoiceGenStatus = value;
	}

	@Mutation
	setConversationVoiceGenerationStatus(value: VoiceGenStatus) {
		this.conversationVoiceGenStatus = value;
	}

	@Mutation
	setMasterTemplateId(value: number) {
		this.masterTemplateId = value;
	}

	@Mutation
	setIsConvNameUnique(value: boolean) {
		this.isConvNameUnique = value;
	}

	@Mutation
	setIsConvNameLengthValid(value: boolean) {
		this.isConvNameLengthValid = value;
	}

	@Mutation
	setIsConvNameValid(value: boolean) {
		this.isConvNameValid = value;
	}

	@Mutation
	setIsConversationComplete(value: boolean) {
		this.isConversationComplete = value;
	}

	@Mutation
	createIntent(intent: Intent) {
		const isAlreadyExists = includes(this.intents.ids, intent.id);
		if (!isAlreadyExists) {
			this.intents.ids.push(intent.id);
		}
		this.intents.directory[intent.id] = intent;
	}

	@Mutation
	editIntent(action: { id: string; intent: Partial<Intent> }) {
		this.intents = {
			ids: this.intents.ids,
			directory: {
				...this.intents.directory,
				[action.id]: { ...this.intents.directory[action.id], ...action.intent }
			}
		};
	}
	@Mutation
	deleteIntent(id: string) {
		this.intents = {
			ids: pull(this.intents.ids, id),
			directory: omit(this.intents.directory, [id])
		};
	}

	@Mutation
	createEntity(entity: Entity) {
		const isAlreadyExists = includes(this.entities.ids, entity.id);
		if (!isAlreadyExists) {
			this.entities.ids.push(entity.id);
		}
		this.entities.directory[entity.id] = entity;
	}

	@Mutation
	editEntity(action: { id: string; entity: Partial<Entity> }) {
		this.entities = {
			ids: this.entities.ids,
			directory: {
				...this.entities.directory,
				[action.id]: { ...this.entities.directory[action.id], ...action.entity }
			}
		};
	}
	@Mutation
	deleteEntity(id: string) {
		this.entities = {
			ids: pull(this.entities.ids, id),
			directory: omit(this.entities.directory, [id])
		};
	}

	@Mutation
	setTestConversationInformation(info: TestConversationDetailsDto) {
		this.conversationTestExtraInfo = { ...this.conversationTestExtraInfo, conversationId: info.id, ...info };
		const meta = info.metaData
			? (JSON.parse(info.metaData) as {
					changes?: string[];
					nodes?: INodes;
					intents?: EntityDataSet<Intent>;
					entities?: EntityDataSet<Entity>;
			  })
			: {};
		this.testConversationChanges = meta.changes || [];
		this.testConverationMetaData = meta as any;
	}

	@Mutation
	setUXTestingAllowed(value: boolean) {
		this.isUXTestingAllowed = value;
	}

	@Action
	addIntent(intent: Intent) {
		this.createIntent(intent);
		!this.changedSinceLastSave && this.setChangedSinceLastSave(true);
	}

	@Action
	updateIntent(action: { id: string; intent: Partial<Intent> }) {
		this.editIntent(action);
		!this.changedSinceLastSave && this.setChangedSinceLastSave(true);
	}

	@Action
	removeIntent(id: string) {
		this.deleteIntent(id);
		!this.changedSinceLastSave && this.setChangedSinceLastSave(true);
	}

	@Action
	addEntity(entity: Entity) {
		this.createEntity(entity);
		!this.changedSinceLastSave && this.setChangedSinceLastSave(true);
	}

	@Action
	updateEntity(action: { id: string; entity: Partial<Entity> }) {
		this.editEntity(action);
		!this.changedSinceLastSave && this.setChangedSinceLastSave(true);
	}

	@Action
	updateEntityName(action: { id: string; name: string }) {
		const entity = this.entities.directory[action.id];
		this.intents.ids.forEach((intentId) => {
			const intent = this.intents.directory[intentId];
			if (intent.entityId === action.id) {
				const newUtterances = intent.utterances.map((utterance) => {
					utterance.value = utterance.value.replace(new RegExp(`{${entity.name}}`, "g"), `{${action.name}}`);
					return utterance;
				});
				this.updateIntent({ id: intent.id, intent: { utterances: newUtterances } });
			}
		});
		this.editEntity({ id: action.id, entity: { name: action.name } });
		!this.changedSinceLastSave && this.setChangedSinceLastSave(true);
	}

	@Action
	removeEntity(id: string) {
		this.deleteEntity(id);
		!this.changedSinceLastSave && this.setChangedSinceLastSave(true);
	}

	@Action
	removeEntityIfNoReferenceExists(entityId: string) {
		const entity = this.entities.directory[entityId];
		const isEntityReferenced = !!this.intents.ids.find((id) => {
			const intent = this.intents.directory[id];
			if (intent.entityId === entity.id) {
				return true;
			}
			return false;
		});
		if (!isEntityReferenced) {
			this.removeEntity(entity.id);
		}
	}

	@Action
	removeEntityValueReferencesFromPath(action: { entityId: string; nodeId: string }) {
		const entity = this.entities.directory[action.entityId];
		const node = this.nodes[action.nodeId];
		const nodeData = node.data as Path;

		const entityValueIds = entity.values.map((value) => value.id);

		const currentEntityValueIdsInPath = nodeData.entityValueIds;

		const updatedEntityValueIds = pull(currentEntityValueIdsInPath, ...entityValueIds);
		this.updateNodeData<Path>({
			id: action.nodeId,
			data: { entityValueIds: updatedEntityValueIds }
		});
	}

	@Action
	public addNode<T = NodeData>(node: ConversationNode<T>): void {
		// save the node
		this.createNode(node);

		// this is just an example
		EventBus.$emit(BuilderEventType.add, node);

		// track changes
		this.trackChanges(node.id);

		!this.changedSinceLastSave && this.setChangedSinceLastSave(true);
	}

	@Action
	public updateNodeData<T = NodeData>(update: INodeUpdate<T>): void {
		this.editNode(update);
		this.trackChanges(update.id);
		!this.changedSinceLastSave && this.setChangedSinceLastSave(true);
	}

	@Action
	public removeNode(nodeId: string): void {
		if (this.nodes[nodeId].data && this.nodes[nodeId].key === 0) {
			const node = this.nodes[nodeId] as ConversationNode<Dialog | Path | Conversation>;
			if (node.data.imageUrl) {
				this.deleteImage(node);
			}
		}
		// delete from nodes
		this.deleteNode(nodeId);
		this.trackChanges(nodeId);
		!this.changedSinceLastSave && this.setChangedSinceLastSave(true);
	}

	@Action
	public removeNodes(nodeIds: string[]): void {
		Modal.confirm({
			title: fmtMsg(ConversationBuilderLocale.ConversationConfirmRemoveNodesWarn),
			okText: fmtMsg(ConversationBuilderLocale.ConversationConfirmRemoveNodeOkTxt),
			cancelText: fmtMsg(ConversationBuilderLocale.ConversationConfirmRemoveNodeCancelTxt),
			onOk: () => {
				nodeIds.forEach((nodeId) => {
					this.removeNode(nodeId);
				});
			},
			onCancel: () => {
				const builderArea = document.getElementById("builder-area");
				if (builderArea) {
					builderArea.focus();
				}
			}
		});
	}

	@Action
	addConnector(update: IConnectorAdd): void {
		this.createConnector(update);
		this.trackChanges(update.sourceId);
		!this.changedSinceLastSave && this.setChangedSinceLastSave(true);
	}

	@Action
	updateCoords(update: { id: string; x: number; y: number }[]): void {
		this.commitCoords(update);
		!this.changedSinceLastSave && this.setChangedSinceLastSave(true);
	}

	@Action
	async saveConversationState(payload: { id: string | number; type: BuilderPageType; force?: boolean }) {
		const { id, type, force } = payload;

		// if current and previous JSON is same, then return
		if (!this.changedSinceLastSave || (this.savingConversation && !force)) {
			return;
		}
		this.setSavingConversation(true);
		this.setChangedSinceLastSave(false); // change at start, because while the change is being saved user may change more
		const model: Partial<UpsertConversationRequestModel> = {};

		// add metadata
		model.metaData = JSON.stringify({
			changes: this.changes,
			nodes: this.nodes,
			intents: this.intents,
			entities: this.entities,
			conversationImageInfo: {
				imageUrl: this.conversationProperties.imageUrl ? this.conversationProperties.imageUrl : null,
				pageId: this.conversationProperties.pageId ? this.conversationProperties.pageId : null,
				contentId: this.conversationProperties.contentId ? this.conversationProperties.contentId : null,
				pageMetaData: this.conversationProperties.pageMetaData ? this.conversationProperties.pageMetaData : null
			}
		});

		//save test converationMeta, if test converation id exists
		if (this.conversationTestExtraInfo.conversationId && this.conversationTestExtraInfo.conversationId > 0) {
			const testMetaDto = cloneDeep(this.testConverationMetaData);
			testMetaDto.changes = this.testConversationChanges;
			model.testConversationId = this.conversationTestExtraInfo.conversationId;
			model.testMetaData = JSON.stringify(testMetaDto);
		}

		// add conversation Id.
		if (this.conversationProperties.id) {
			model.conversationId = this.conversationProperties.id;
		}

		// add conversation props
		model.name = this.conversationProperties.name;
		model.description = this.conversationProperties.description;
		model.imageUrl = this.conversationProperties.imageUrl as any;
		model.pageId = this.conversationProperties.pageId as any;
		model.pageMetaData = this.conversationProperties.pageMetaData as any;
		let isMasterConveration = false;
		// Add conversation parent entity info
		switch (type) {
			case BuilderPageType.ContentInfo:
				model.contentId = id;
				break;
			case BuilderPageType.ExposureInfo:
				model.exposureId = id;
				break;
			case BuilderPageType.MasterInfo:
				model.masterConversationId = id;
				isMasterConveration = true;
				break;
		}

		try {
			// if conversationId is not present, but returned by API, set conversationId
			const { conversationId, masterConversationId } = await ConversationBuilderService.saveConversation(
				model as any
			);

			const conversationIdwasNotPresent = !this.conversationProperties.id || this.conversationProperties.id <= 0;
			!this.conversationProperties.id && this.setConversationProps({ id: conversationId });
			!this.masterTemplateId && masterConversationId && this.setMasterTemplateId(masterConversationId);

			// fetch test conversation metadata
			// will only be executed when converation is created first time.
			if (!isMasterConveration && conversationId > 0 && conversationIdwasNotPresent) {
				await this.fetchTestConversationMeta(conversationId);
			}

			this.setSavingConversation(false);
		} catch (e) {
			this.setChangedSinceLastSave(true);
			this.setSavingConversation(false);
		}
	}

	@Action
	async fetchTestConversationMeta(conversationId: number): Promise<void> {
		// fetch test conversation metadata
		const testConversation: TestConversationDetailsDto = await ConversationBuilderService.getTestConversation(
			conversationId
		);
		this.setTestConversationInformation(testConversation);
	}

	@Action
	async isNameUnique(name: string): Promise<boolean> {
		const isUnique = await ConversationBuilderService.isUniqueName(name, this.conversationProperties.id);
		this.setIsConvNameUnique(isUnique);
		return isUnique;
	}

	@Action
	async isNameLengthValid(name: string): Promise<boolean> {
		const isValid = name.length > CLUValidations.projectNameLengthLimit ? false : true;
		this.setIsConvNameLengthValid(isValid);
		return isValid;
	}

	@Action
	async isNameValid(name: string): Promise<boolean> {
		const isValid = CLUValidations.conversationNameRegex.test(name);
		this.setIsConvNameValid(isValid);
		return isValid;
	}

	@Action
	async getConversationCompleteState(id: number): Promise<boolean> {
		const isComplete = await ConversationBuilderService.getConversationCompleteStatus(id);
		this.setIsConversationComplete(isComplete);
		return isComplete;
	}

	@Action
	async fetchConversation(payload: { id: string | number; type: BuilderPageType }) {
		const { id, type } = payload;
		let params = {};
		let isMaterConversation = false;
		switch (type) {
			case BuilderPageType.ContentInfo:
				params = { contentId: id };
				break;
			case BuilderPageType.ExposureInfo:
				params = { exposureId: id };
				break;
			case BuilderPageType.MasterInfo:
				isMaterConversation = true;
				params = { masterId: id };
		}
		try {
			const details: ConversationDetailResponseModel = await ConversationBuilderService.getConversation(params);
			const commonEntites = await ConversationBuilderService.getCommonEntities();
			const commonIntents = await ConversationBuilderService.getCommonIntents();
			const interactionStyles = await ConversationBuilderService.getInteractionStyles();

			if (details.conversationId > 0) {
				if (!isMaterConversation) {
					// fetch test converation information
					await this.fetchTestConversationMeta(details.conversationId);
				}
				// set main converation information
				const metaData: IMetaData = JSON.parse(details.metaData);

				if (!metaData.intents) {
					metaData.intents = getEntitySetDefaultValue();
				}
				if (!metaData.entities) {
					metaData.entities = getEntitySetDefaultValue();
				}

				// Merging app intent with common intent
				mergeCommonIntentIntoAppIntents(commonIntents, commonEntites, metaData.intents, metaData.entities);

				// Merging app entity with common entity
				mergeCommonEntitiesIntoAppEntities(commonEntites, metaData.entities, metaData.intents);

				// Invalidate entity value ids in each path
				invalidateEntityValueIdsInPath(metaData.nodes, metaData.intents, metaData.entities);

				// add default interaction style for old conversations
				const updatedMetaData = updateMetaData(metaData);

				this.setMetaData(updatedMetaData);

				this.setConversationProps({
					id: details.conversationId,
					name: details.name || "",
					description: details.description || "",
					imageUrl: updatedMetaData.conversationImageInfo?.imageUrl,
					pageId: updatedMetaData.conversationImageInfo?.pageId,
					pageMetaData: updatedMetaData.conversationImageInfo?.pageMetaData,
					contentId: updatedMetaData.conversationImageInfo?.contentId
				});

				this.setChangedSinceLastSave(true);
			}

			// Adding common intents and entites
			this.setCommonEntitiesIntentsAndStyles({
				intents: commonIntents,
				entities: commonEntites,
				styles: interactionStyles
			});

			// set Extra information (Used for image upload and breadcrumb)
			const {
				title,
				exposureSequence,
				versionGroupId,
				versionGroup,
				curriculumType,
				luisAppId,
				unitId,
				conversationId,
				parentContentId,
				exposureSequenceInMethodology,
				publishStatusId,
				cluAccountId
			} = details;
			this.setConversationExtraInfo({
				title,
				exposureSequence,
				versionGroupId,
				versionGroup,
				curriculumType,
				luisAppId,
				unitId,
				conversationId,
				parentContentId,
				exposureSequenceInMethodology,
				publishStatusId,
				cluAccountId
			});
		} catch (e) {
			console.error(e);
			//#TODO: Redirect to previous page
		}
	}

	// get the conversation of the content
	@Action
	async fetchContentConversation(id: string) {
		if (typeof id !== "string" || !id.length) {
			return;
		}
		const info = await ConversationBuilderService.getConversation({ contentId: id });
		if (info.conversationId > 0) {
			const meta: IMetaData = JSON.parse(info.metaData);
			this.setConversationProps({
				id: info.conversationId,
				name: info.name || "",
				description: info.description || "",
				imageUrl: meta.conversationImageInfo?.imageUrl,
				pageId: meta.conversationImageInfo?.pageId,
				pageMetaData: meta.conversationImageInfo?.pageMetaData,
				contentId: meta.conversationImageInfo?.contentId
			});
		} else {
			this.setConversationProps({
				id: info.conversationId,
				name: info.name || "",
				description: info.description || "",
				imageUrl: null,
				pageId: null,
				pageMetaData: null,
				contentId: null
			});
		}

		const metaData = JSON.parse(info.metaData);
		this.setMetaData({
			changes: [],
			nodes: metaData?.nodes,
			entities: metaData?.entities,
			intents: metaData?.intents
		});
	}

	// Uploads an image/Audio and saves to node
	// 'id' param is for contentId or exposureId or unitId

	@Action
	async uploadFile(payload: {
		imageInfo: Partial<{ name: string; type: ConversationNodeType; pageId: string; pageMetaData: ImageMetaData }>;
		type: BuilderPageType;
		node?: ConversationNode;
		id: string | number;
	}): Promise<void> {
		const { imageInfo, type, node, id } = payload;
		//const isConversation = !node;
		let conversationId = this.conversationProperties.id;
		let imageUrl = "";
		const { pageId } = imageInfo;
		if (!pageId) return;
		try {
			if (conversationId === null) {
				await this.saveConversationState({ id, type });
				conversationId = this.conversationProperties.id;
			}
			if (node && this.nodes[node.id]) {
				const { id, type } = node;
				imageUrl = await ConversationBuilderService.uploadImage(
					id,
					type,
					pageId,
					JSON.stringify(imageInfo.pageMetaData),
					this.conversationProperties.name,
					node.data.name
				);
				this.updateNodeData({ id: node.id, data: { imageUrl } });
				!this.changedSinceLastSave && this.setChangedSinceLastSave(true);
			}
			// else if (isConversation && this.conversationProperties.id) {
			// 	imageUrl = await ConversationBuilderService.uploadImage(
			// 		this.conversationProperties.id,
			// 		ConversationNodeType.Conversation,
			// 		pageId,
			// 		""
			// 	);
			// 	this.setConversationProps({ imageUrl });
			// }
		} catch (error) {
			MessageHelper.Message(NotificationType.Failed, fmtMsg(CommonLocale.CommonHttpServerError));
		}
	}

	@Action
	async deleteImage(node?: ConversationNode<Dialog | Path | Conversation>) {
		if (node) {
			if (!node.data.imageUrl) return;
			try {
				//handle the old logic: Image clone instead of reference
				if (isOldConversationImage(node.data.imageUrl)) {
					if (node.key === 0) {
						await this.removeImage(node.data.imageUrl as string);
					}
					this.updateNodeData({
						id: node.id,
						data: { imageUrl: null, pageMetaData: null, contentId: null, pageId: null }
					});
				} else {
					if (!this.conversationProperties.id) return;
					const result = await ConversationBuilderService.removeImage({
						conversationId: this.conversationProperties.id,
						entityTypeId: node.type,
						entityId: node.id,
						entityName: node.data.name
					});
					if (result) {
						this.updateNodeData({
							id: node.id,
							data: { imageUrl: null, pageMetaData: null, contentId: null, pageId: null }
						});
					} else {
						MessageHelper.Message(NotificationType.Failed, fmtMsg(CommonLocale.CommonHttpServerError));
					}
				}
			} catch (error) {
				MessageHelper.Message(NotificationType.Failed, fmtMsg(CommonLocale.CommonHttpServerError));
			}
		} else {
			const cb = () => {
				!this.changedSinceLastSave && this.setChangedSinceLastSave(true);
				this.setConversationProps({
					imageUrl: null
				});
			};
			try {
				if (!this.conversationProperties.imageUrl) return;
				if (isOldConversationImage(this.conversationProperties.imageUrl)) {
					cb();
				} else {
					if (!this.conversationProperties.id) return;
					const result = await ConversationBuilderService.removeImage({
						conversationId: this.conversationProperties.id,
						entityTypeId: ConversationNodeType.Conversation,
						entityId: this.conversationProperties.id
					});
					if (result) {
						cb();
					} else {
						MessageHelper.Message(NotificationType.Failed, fmtMsg(CommonLocale.CommonHttpServerError));
					}
				}
			} catch (error) {
				MessageHelper.Message(NotificationType.Failed, fmtMsg(CommonLocale.CommonHttpServerError));
			}
		}
	}

	@Action
	async downloadFile(url: string) {
		await this.getDownloadSasURL(); // gets the sas url
		let targetContainer = ContainerType.Page;
		const isCloneImage = isOldConversationImage(url);
		if (isCloneImage) {
			//handle the old logic: Image clone instead of reference
			targetContainer = ContainerType.File;
		}
		const sasURL = this.downloadSASURL?.find((f) => f.type === targetContainer) as SignatureResoureModel;
		const blobURL = await this.generateBlockBlobClient({ sasUrl: sasURL.url, container: sasURL.name, path: url });
		const request = await blobURL.download();
		const blob = await request.blobBody;
		if (blob) {
			FileSaver.saveAs(blob, getActualFileNameAndType(url));
		} else {
			throw "File is not found.";
		}
	}

	@Action
	async generateBlockBlobClient(args: { sasUrl: string; container: string; path: string }): Promise<BlockBlobClient> {
		const { sasUrl, container, path } = args;
		const pipeline = newPipeline(new AnonymousCredential(), {
			retryOptions: { maxTries: 4 } // Retry options
		});
		const serviceClient = new BlobServiceClient(sasUrl, pipeline);
		const containerClient = serviceClient.getContainerClient(container);
		return containerClient.getBlockBlobClient(path);
	}

	@Action
	async getSasURL() {
		// gets sas URL for uploads
		if (!this.sasURL || moment(this.sasURLFetchedAt).diff(moment(), "h") > 1) {
			// fetch sas url
			this.setSasURL(await AdminService.getUploadSasURL());
		}
	}

	@Action
	async getDownloadSasURL() {
		// gets sas URL for uploads
		if (!this.downloadSASURL || moment(this.downloadSASURLFetchAt).diff(moment(), "h") > 1) {
			// fetch sas url
			this.setDownloadSasURL(await AdminService.getDownloadSasURL());
		}
	}

	@Action
	getFilePath(args: {
		conversationId: number;
		file: File;
		type: BuilderPageType;
		nodeType: ConversationNodeType;
		info: Partial<ConversationDetailResponseModel>;
	}): string {
		const { conversationId, file, type, nodeType, info } = args;
		const name = v4() + file.name;
		// path could be generated for content conversation's or exposure conversations or unit conversations
		// dialogs, intents, or for conversation itself
		const dialogOrPath = ConversationNodeImagePath[nodeType] ? ConversationNodeImagePath[nodeType] : "Others";
		if (type === BuilderPageType.ContentInfo) {
			return `${info.versionGroup}/Conversations/${conversationId}/${dialogOrPath}/${name}`;
		} else if (type === BuilderPageType.ExposureInfo) {
			return `${info.versionGroup}/Conversations/${conversationId}/${dialogOrPath}/${name}`;
		} else if (type === BuilderPageType.MasterInfo) {
			return `masterdialog/${conversationId}/${dialogOrPath}/${name}`;
		}
		// #TODO handle unit conversation path case would be like "GrapeSeed|LittleSeed/<unit>/<converationId>/dialog|Path/image.jpg"
		return `${info.versionGroup}/Conversations/Others/${name}`;
	}

	@Action
	async removeImage(url: string) {
		await ConversationBuilderService.deleteImage(url);
	}

	// only call it when in conversation builder
	@Action
	async copyImages(args: {
		nodeTypeWithUrl: { type: ConversationNodeType; url: string; nodeId: string }[];
		id: string | number;
		type: BuilderPageType;
	}): Promise<{ [nodeId: string]: { newURL: string | undefined; oldURL: string } } | null> {
		const { id, type, nodeTypeWithUrl } = args;
		const response: { [nodeId: string]: { newURL: string | undefined; oldURL: string } } = {};
		try {
			if (!this.conversationProperties.id) {
				await this.saveConversationState({ id, type });
			}

			const info: Partial<ConversationDetailResponseModel> = deepClone(this.conversationExtraInfo);
			const request: CopyImageRequestModel[] = [];

			for (const index in nodeTypeWithUrl) {
				const node = nodeTypeWithUrl[index];
				// get the destination path
				const file = new File([], getActualFileNameAndType(node.url));
				const destPath = await this.getFilePath({
					conversationId: this.conversationProperties.id as number,
					file,
					type,
					nodeType: node.type,
					info
				});
				request.push({
					identifier: node.nodeId,
					sourceURL: node.url,
					targetURL: destPath
				});
			}
			const apiResponse = await ConversationBuilderService.copyImages(request);

			request.forEach((r) => {
				if (apiResponse[r.identifier]) {
					response[r.identifier] = { newURL: r.targetURL, oldURL: r.sourceURL };
				} else {
					response[r.identifier] = { newURL: undefined, oldURL: r.sourceURL };
				}
			});
			return response;
		} catch (e) {
			return null;
		}
	}

	@Action
	async getTrainingStatus() {
		const { conversationId } = this.conversationExtraInfo;
		if (!conversationId) {
			this.setCreateOrPublishingConversation(false);
			return;
		}

		const response = await ConversationBuilderService.getPublishStatus(conversationId);

		if (response === ConversationPublishStatus.Deployed) {
			this.setTrainingStatus(ConversationPublishStatus.Deployed);
			this.setCreateOrPublishingConversation(false);
		} else if (response === ConversationPublishStatus.Failed) {
			this.setTrainingStatus(ConversationPublishStatus.Failed);
			this.setCreateOrPublishingConversation(false);
		} else {
			if (
				this.currentPublishStatus === ConversationPublishStatus.None &&
				response <= ConversationPublishStatus.Trained
			) {
				this.setTrainingStatus(ConversationPublishStatus.Initial);
			} else {
				response >= ConversationPublishStatus.Initial &&
					response <= ConversationPublishStatus.Trained &&
					this.setTrainingStatus(ConversationPublishStatus.Initial);
			}
			await new Promise((res) => setTimeout(res, 5000));
			this.getTrainingStatus();
		}
	}

	/**
	 * Gets the Status of Test conversation creation and publishing.
	 * @returns
	 */
	@Action
	async getTrainingStatusOfTestConversation(calledFromResolver: boolean = false) {
		const { conversationId } = this.conversationTestExtraInfo; // change to testconv
		if (!conversationId) {
			this.setCreateOrPublisingTestConversation(false);
			return;
		}
		const response = await ConversationBuilderService.getPublishStatus(conversationId);

		if (response === ConversationPublishStatus.Deployed) {
			if (calledFromResolver) return;
			this.setTestTrainingStatus(ConversationPublishStatus.Deployed);
			this.setCreateOrPublisingTestConversation(false);
		} else if (response === ConversationPublishStatus.Failed) {
			this.setTestTrainingStatus(ConversationPublishStatus.Failed);
			this.setCreateOrPublisingTestConversation(false);
		} else {
			if (
				this.currentTestPublishStatus === ConversationPublishStatus.None &&
				response <= ConversationPublishStatus.Trained
			) {
				this.setTestTrainingStatus(ConversationPublishStatus.Initial);
			} else {
				response >= ConversationPublishStatus.Initial &&
					response <= ConversationPublishStatus.Trained &&
					this.setTestTrainingStatus(ConversationPublishStatus.Initial);
			}
			await new Promise((res) => setTimeout(res, 5000));
			this.getTrainingStatusOfTestConversation();
		}
	}

	/**
	 * Create/Publishes the Test version of a conversation.
	 * @param args
	 */
	@Action
	async CreateOrTrainTestConversation(args: { id: string; pagetype: BuilderPageType }) {
		const { id, pagetype } = args;

		try {
			this.setTestTrainingStatus(ConversationPublishStatus.Initial);
			// save main conversation changes if not alrady saved
			await this.saveConversationState({ id, type: pagetype, force: true });

			this.setCreateOrPublisingTestConversation(true);

			const {
				luisAccountId,
				luisAppId,
				appVersion,
				metaData,
				conversationId // Test conversationId
			} = await ConversationBuilderService.createOrTrainTestConversation(this.conversationProperties.id!);

			this.setTestConversationInformation({
				luisAppId,
				luisAccountId,
				metaData: JSON.stringify(metaData),
				appVersion,
				id: conversationId
			});
			this.getTrainingStatusOfTestConversation();
		} catch (e) {
			if (e.body) {
				const { error_code, error_description } = e.body;
				showErrorMessage(
					{ error_code, error_description },
					fmtMsg(CommonLocale.CommonContentConversationAssociationPublishMessage)
				);
			}
			this.setTestTrainingStatus(ConversationPublishStatus.None);
			this.setCreateOrPublisingTestConversation(false);
		}
	}

	@Action
	async CreateOrTrainConversation(args: { id: string; pagetype: BuilderPageType, isTrained: boolean; }) {
		const { id, pagetype, isTrained } = args;

		try {
			this.setTrainingStatus(ConversationPublishStatus.Initial);

			// save changes
			await this.saveConversationState({ id, type: pagetype, force: true });

			this.setCreateOrPublishingConversation(true);
			const {
				luisAccountId,
				luisAppId,
				appVersion,
				metaData,
				conversationId
			} = await ConversationBuilderService.createOrTrainConversation(
				this.conversationProperties.id!,
				pagetype === BuilderPageType.ExposureInfo,
				isTrained
			);
			this.setMetaData(metaData);
			this.setConversationExtraInfo({
				...this.conversationExtraInfo,
				luisAccountId,
				luisAppId,
				appVersion,
				conversationId
			});
			this.getTrainingStatus();
		} catch (e) {
			if (e.body) {
				const { error_code, error_description } = e.body;
				showErrorMessage(
					{ error_code, error_description },
					fmtMsg(CommonLocale.CommonContentConversationAssociationPublishMessage)
				);
			}
			this.setTrainingStatus(ConversationPublishStatus.None);
			this.setCreateOrPublishingConversation(false);
		}
	}

	@Action
	async getTestVoiceGenerationStatus(fromResolver = false) {
		if (!this.conversationTestExtraInfo.conversationId || !this.isUXTestingAllowed) {
			return;
		}
		const status = await ConversationBuilderService.getConverationVoiceGenerationStatus(
			this.conversationTestExtraInfo.conversationId as number
		);

		if (status !== null && (!fromResolver || status !== VoiceGenStatus.Success)) {
			this.setTestVoiceGenerationStatus(status);
		}

		if (status === VoiceGenStatus.Queued || status === VoiceGenStatus.Running) {
			await new Promise((res) => setTimeout(res, 5000));
			this.getTestVoiceGenerationStatus();
		}
	}

	@Action
	async getConversationVoiceGenerationStatus(fromResolver = false) {
		if (!this.conversationExtraInfo.conversationId) {
			return;
		}
		const status = await ConversationBuilderService.getConverationVoiceGenerationStatus(
			this.conversationExtraInfo.conversationId as number
		);

		if (status !== null && (!fromResolver || status !== VoiceGenStatus.Success)) {
			this.setConversationVoiceGenerationStatus(status);
		}

		if (status === VoiceGenStatus.Queued || status === VoiceGenStatus.Running) {
			await new Promise((res) => setTimeout(res, 5000));
			this.getConversationVoiceGenerationStatus();
		}
	}

	@Action
	async getIsUXTestingAllowed() {
		const userId = GLGlobal.loginInfo()?.profile?.sub;
		if (!userId) {
			return;
		}
		const isAllowed = await AccountService.isTestingAllowed(userId);
		if (isAllowed == true) {
			this.setUXTestingAllowed(true);
		}
	}

	@Action
	async isVcsInternalUser() {
		const userRole = GLGlobal.loginInfo().profile.role;
		if (Array.isArray(userRole)) {
			if (userRole.includes(RoleName.contentAdmin) || userRole.includes(RoleName.systemAdmin)) {
				this.setUXTestingAllowed(true);
				return;
			}
		} else if (userRole == RoleName.contentAdmin || userRole == RoleName.systemAdmin) {
			this.setUXTestingAllowed(true);
			return;
		}
		this.setUXTestingAllowed(false);
	}

	@Action
	async pasteMetaData(args: {
		metaData: IMetaData;
		svgSize: [number, number];
		pageType: BuilderPageType;
		id: string;
		masterConversationName?: string;
	}) {
		const { svgSize, pageType, id, metaData, masterConversationName } = args;

		const sourceIntents = metaData.intents.directory;

		Object.keys(sourceIntents).forEach((sourceIntentId) => {
			const sourceIntent = sourceIntents[sourceIntentId];

			// If same intent is already present in destination.
			// then ignore source intent, because destination intent will have updated intent, utterance and entity.
			const isPresent = this.intents.ids.some((id) => id === sourceIntent.id);
			if (!isPresent) {
				this.addIntent({
					...sourceIntent,
					key: 0,
					utterances: sourceIntent.utterances.map((item) => ({ ...item, key: 0 }))
				}); // Since it is a new intent in destination, key must be 0.

				if (sourceIntent.entityId) {
					const isEntityPresent = this.entities.ids.some((id) => id === sourceIntent.entityId);
					const sourceEntity = metaData.entities.directory[sourceIntent.entityId];

					// If entity is used by source intent and it is already being used by destination.
					if (isEntityPresent) {
						// Find extra entity values in source and delete it from paths.
						const destinationEntity = this.entities.directory[sourceIntent.entityId];
						const extraEntityValues = differenceBy(sourceEntity.values, destinationEntity.values, "id");

						const sourcePaths = Object.keys(metaData.nodes)
							.filter((key) => metaData.nodes[key].type === ConversationNodeType.Path)
							.map((key) => metaData.nodes[key] as ConversationNode<Path>);

						sourcePaths.forEach((path) => {
							path.data.entityValueIds = path.data.entityValueIds.filter(
								(evId) => !extraEntityValues.some((e) => e.id === evId)
							);
						});
					} else {
						this.addEntity({
							...sourceEntity,
							key: 0,
							values: sourceEntity.values.map((item) => ({ ...item, key: 0 }))
						}); // Since it is a new entity in destination, key must be 0.
					}
				}
			}
		});

		return this.pasteNodes({ nodes: metaData.nodes, svgSize, pageType, id, masterConversationName });
	}

	@Action
	async pasteNodes(args: {
		nodes: INodes;
		svgSize: [number, number];
		pageType: BuilderPageType;
		id: string;
		masterConversationName?: string;
	}): Promise<{ err?: string; }> {
		const { svgSize, pageType, id, masterConversationName } = args;
		const cloneNodes = cloneDeep(args.nodes);
		const ids: string[] = [];
		Object.keys(cloneNodes).forEach((key) => {
			ids.push(key);
			const node = cloneNodes[key] as ConversationNode;
			node.key = 0; // since pasted nodes are new nodes, the key must be 0.
			node.coords.y += svgSize[1];
		});
		const { nodes, mapIds } = assignNewIds(cloneNodes, ids);

		// update images
		const updatedNodes = await this.copyImagesOfNodes({ nodes, pageType, id, mapIds, masterConversationName });
		if (!updatedNodes) {
			return { err: "Not able to copy images from master dialog. Please try again." };
		}

		// update order field
		// find current order max assigned till
		const currentNodes = Object.entries(this.nodes);

		if (currentNodes.length) {
			const dialogNodes = currentNodes
				.filter(
					([_, node]) =>
						node.type === ConversationNodeType.Conversation || node.type === ConversationNodeType.Dialog
				)
				.map(([_, node]) => (node as ConversationNode<Dialog>).data.order);

			const pathNodes = currentNodes
				.filter(([_, node]) => node.type === ConversationNodeType.Path)
				.map(([_, node]) => (node as ConversationNode<Path>).data.order);

			let maxDialogOrder = dialogNodes.length ? Math.max(...dialogNodes) : 0;

			let maxPathOrder = pathNodes.length ? Math.max(...pathNodes) : 0;

			// assign new order
			Object.entries(nodes).forEach(([key]) => {
				const node = nodes[key] as ConversationNode<Path | Dialog>;
				switch (node.type) {
					case ConversationNodeType.Path:
						node.data.order = ++maxPathOrder;
						break;
					case ConversationNodeType.Conversation:
						// Always have order 0 for start node. Only 1 start node is allowed. So its okay, if json has start node > 1 with order = 0.
						node.data.order = 0;
						break;
					case ConversationNodeType.Dialog:
						node.data.order = ++maxDialogOrder;
						break;
				}
			});
		}

		this.addTemplate(nodes);
		Object.keys(nodes).forEach((key) => this.trackChanges(key));
		this.setChangedSinceLastSave(true);
		return {};
	}

	@Mutation
	public async copyImageReferences(action: {
		images: {
			type: ConversationNodeType;
			url: string;
			nodeId: string;
		}[];
		mapIds: { [key: string]: string; };
	}) {
		const { images, mapIds } = action;
		if (images.length === 0) return true;

		const cloneData = images
			.filter((img) => !isOldConversationImage(img.url))
			.map((img) => ({
				fromNodeId: mapIds[img.nodeId],
				toNodeId: img.nodeId,
				nodeType: img.type
			}));

		return await ConversationBuilderService.cloneImageReference({ nodes: cloneData });
	}

	@Action
	public async copyImagesOfNodes(args: {
		nodes: INodes;
		pageType: BuilderPageType;
		id: string;
		mapIds: { [key: string]: string; };
		masterConversationName?: string;
	}): Promise<INodes | undefined> {
		const { nodes, pageType, id, mapIds, masterConversationName } = args;
		const tNodes = (nodes as any) as { [x: string]: ConversationNode<Path | Dialog>; };
		const conversationName = this.conversationProperties.name;

		// find all the nodes with images
		const nodesWithImages = Object.entries(tNodes)
			.filter(
				(f) =>
					f[1].type === ConversationNodeType.Dialog ||
					f[1].type === ConversationNodeType.Path ||
					f[1].type === ConversationNodeType.Conversation
			)
			.map((m) => m[1] as ConversationNode<Dialog | Path | Conversation>)
			.filter((m: any) => m.data && m.data.imageUrl);

		// return if there is not any node with image
		if (!nodesWithImages.length) {
			await ConversationBuilderService.addConversationTemplate({ conversationName, masterConversationName });
			return tNodes;
		}

		// create a object with mapping between 'node's id', it's type and imageUrl
		const images = nodesWithImages.map((n) => ({ type: n.type, url: n.data.imageUrl as string, nodeId: n.id }));

		// images that required to clone reference
		const needCloneRefs = images.filter((img) => !isOldConversationImage(img.url));
		await this.copyImageReferences({
			images: needCloneRefs,
			mapIds
		});

		// images that required to clone
		const needCloneImages = images.filter((img) => isOldConversationImage(img.url));
		// call the function to copy images
		const updatedImageUrl = await this.copyImages({
			nodeTypeWithUrl: needCloneImages,
			type: pageType,
			id: id
		});

		// Update the new image url to respective nodes
		if (updatedImageUrl) {
			needCloneImages.forEach((i) => {
				const newUrl = updatedImageUrl[i.nodeId].newURL;
				if (newUrl) {
					tNodes[i.nodeId].data.imageUrl = newUrl;
				} else {
					tNodes[i.nodeId].data.imageUrl = null;
				}
			});
		} else {
			return undefined;
		}
		await ConversationBuilderService.addConversationTemplate({ conversationName, masterConversationName });
		return tNodes;
	}
}
